import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import {
  UserCheck,
  Clock,
  Bell,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const StaffSidebar: React.FC = () => {
  const navigate = useNavigate();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    if (!auth.currentUser) return;

    // Set up real-time listener for staff-specific unread notifications
    const notificationsQuery = query(
      collection(db, "notifications"),
      where("userId", "==", auth.currentUser.uid),
      where("read", "==", false)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      setUnreadNotifications(snapshot.docs.length);
    }, (error) => {
      console.error("Error fetching notification count:", error);
      setUnreadNotifications(0); // Set to 0 on error to avoid UI issues
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  // Redirect to enrollment-management if current path is /dashboard/staff (root)
  React.useEffect(() => {
    if (window.location.pathname === "/dashboard/staff" || window.location.pathname === "/dashboard/staff/") {
      navigate("/dashboard/staff/enrollment-management", { replace: true });
    }
  }, [navigate]);

  const links = [
    { to: "/dashboard/staff/enrollment-management", label: "Enrollment", icon: <UserCheck className="h-5 w-5" /> },
    { to: "/dashboard/staff/time-slots", label: "Time Slots", icon: <Clock className="h-5 w-5" /> },
    { 
      to: "/dashboard/staff/notifications", 
      label: "Notifications", 
      icon: <Bell className="h-5 w-5" />,
      badge: unreadNotifications > 0 ? unreadNotifications : null
    },
  ];
  return (
    <aside className={`h-full bg-background border-r p-4 flex flex-col transition-all duration-300 relative ${isCollapsed ? 'w-20' : 'w-64'}`}>
      {/* Toggle button positioned in the middle of the sidebar */}
      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-1/2 transform -translate-y-1/2 bg-background border rounded-full p-1 hover:bg-muted transition-colors z-10 shadow-sm"
      >
        {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>
      
      <div className="mb-8">
        {!isCollapsed && (
          <div>
            <h2 className="text-2xl font-bold">MSS Driving School</h2>
            <p className="text-muted-foreground text-sm">Staff Panel</p>
          </div>
        )}
      </div>
      <nav className="space-y-1 flex-1">
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            className={({ isActive }) =>
              `w-full flex items-center px-3 py-2 rounded-md transition-colors ${isActive ? "bg-muted text-primary" : "hover:bg-muted/50"} ${isCollapsed ? 'justify-center' : ''}`
            }
            title={isCollapsed ? link.label : undefined}
          >
            <div className={`flex items-center ${isCollapsed ? '' : 'w-full'}`}>
              <div className={`flex items-center ${isCollapsed ? '' : 'flex-1'}`}>
                {link.icon}
                {!isCollapsed && <span className="ml-3">{link.label}</span>}
              </div>
              {link.badge && !isCollapsed && (
                <Badge 
                  variant="secondary" 
                  className="ml-2 bg-red-100 text-red-700 text-xs min-w-[20px] h-5 flex items-center justify-center"
                >
                  {link.badge > 99 ? "99+" : link.badge}
                </Badge>
              )}
            </div>
          </NavLink>
        ))}
      </nav>
      <button
        onClick={handleLogout}
        className={`w-full flex items-center px-3 py-2 rounded-md mt-4 text-left hover:bg-muted/50 transition-colors ${isCollapsed ? 'justify-center' : ''}`}
        title={isCollapsed ? "Logout" : undefined}
      >
        <LogOut className="h-5 w-5" />
        {!isCollapsed && <span className="ml-3">Logout</span>}
      </button>
    </aside>
  );
};

export default StaffSidebar;
